import { useState } from "react";
import { Loader2, TriangleAlert } from "lucide-react";

import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import {
  outcomeAllowsJobCreation,
  useLiveEstimateVocabulary,
  type EstimateOutcomeOption,
  type EstimateWriteIntent,
} from "@shared-core/domain/estimate-controls";

import { Badge } from "../../primitives/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { OutcomeConfirmStep } from "./OutcomeConfirmStep";

/**
 * Park / chase — recording where a live conversation stands, without closing
 * the estimate.
 *
 * Split out of Record outcome deliberately: "Tender", "No Feedback" and
 * "On hold with customer" are states, not outcomes. The reporting classifier
 * matches none of them, so the estimate stays open and still ages into stale —
 * offering them alongside the ways an estimate ends is how a CSR parks a quote
 * believing they cancelled it. A separate control makes the question explicit:
 * this one asks "where is the conversation?", the neighbour asks "how did it
 * end?".
 *
 * Same pick-then-confirm mechanics and the same setstatus call as Record
 * outcome — the write underneath is identical, only the question differs.
 */

type Props = {
  estimate: EstimateDetailViewItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once a status has actually been written to Tharstern. */
  onParked: (intent: EstimateWriteIntent) => void;
};

/**
 * The picker step's own subcomponent, so `useLiveEstimateVocabulary`'s fetch
 * mounts (and refetches) exactly when Radix mounts this dialog's content —
 * see the hook's own docstring.
 */
function ParkPicker({
  onPick,
}: {
  onPick: (option: EstimateOutcomeOption) => void;
}) {
  const vocabulary = useLiveEstimateVocabulary();

  if (vocabulary.status === "loading") {
    return (
      <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading Tharstern&rsquo;s current status list…
      </p>
    );
  }

  if (vocabulary.status === "error") {
    return (
      <p className="flex items-start gap-2 rounded-md border border-[color:var(--color-error)] px-3 py-2 text-sm text-[color:var(--color-error)]">
        <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Could not load Tharstern&rsquo;s current status list, so nothing here
          can be safely offered. Close this and try again.
        </span>
      </p>
    );
  }

  const group = vocabulary.parkGroup;
  return (
    <div className="space-y-3 py-2">
      {/* The two honest statements this control is built around: none of
          these close the estimate, and they disagree on whether a job can
          still be raised. Straight from the headless group so the wording
          cannot drift from what the classifier does. */}
      <p className="text-xs text-muted-foreground">{group.explanation}</p>
      <ul className="space-y-1">
        {group.options.map((option) => (
          <li key={option.statusRef}>
            <button
              type="button"
              onClick={() => onPick(option)}
              className="w-full text-left flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-[color:var(--color-surface-2,rgba(0,0,0,0.03))] transition-colors"
            >
              <span className="flex-1 min-w-0 truncate">
                {option.statusRef}
              </span>
              {/* Which options the business actually uses. `null` (a
                  live-only string the 180-day snapshot never saw) renders
                  neither badge nor count — an unknown count is not the same
                  claim as a known zero. */}
              {option.liveRowCount === 0 ? (
                <Badge variant="neutral">Not used recently</Badge>
              ) : option.liveRowCount === null ? null : (
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {option.liveRowCount.toLocaleString()} in 6 months
                </span>
              )}
              {!outcomeAllowsJobCreation(option) && (
                <Badge variant="warning">Blocks a job</Badge>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ParkChaseDialog({
  estimate,
  open,
  onOpenChange,
  onParked,
}: Props) {
  const [picked, setPicked] = useState<EstimateOutcomeOption | null>(null);
  const sourceEstimateId = estimate.information.source_estimate_id;

  function close() {
    onOpenChange(false);
    setPicked(null);
  }

  // Unreachable in practice: EstimateControls only mounts this dialog once
  // estimateControlsPresentation has confirmed a source id exists (the
  // Park / chase button is disabled-with-a-reason otherwise).
  if (sourceEstimateId == null) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        {picked === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Park / chase</DialogTitle>
              <DialogDescription>
                Record where the conversation stands while the estimate stays
                live. How it ended lives under Record outcome.
              </DialogDescription>
            </DialogHeader>
            <ParkPicker onPick={setPicked} />
          </>
        ) : (
          <OutcomeConfirmStep
            // Keyed so going back and picking a different reason starts a
            // fresh confirm step rather than reusing stale local state.
            key={picked.statusRef}
            estimate={estimate}
            sourceEstimateId={sourceEstimateId}
            option={picked}
            onBack={() => setPicked(null)}
            onConfirm={(intent) => {
              onParked(intent);
              close();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
